import * as React from 'react';
import { CssVarsProvider, useColorScheme } from '@mui/joy/styles';
import GlobalStyles from '@mui/joy/GlobalStyles';
import CssBaseline from '@mui/joy/CssBaseline';
import Box from '@mui/joy/Box';
import Button from '@mui/joy/Button';
import Checkbox from '@mui/joy/Checkbox';
import Divider from '@mui/joy/Divider';
import FormControl from '@mui/joy/FormControl';
import FormLabel, { formLabelClasses } from '@mui/joy/FormLabel';
import IconButton, { IconButtonProps } from '@mui/joy/IconButton';
import Link from '@mui/joy/Link';
import Input from '@mui/joy/Input';
import Typography from '@mui/joy/Typography';
import Stack from '@mui/joy/Stack';
import DarkModeRoundedIcon from '@mui/icons-material/DarkModeRounded';
import LightModeRoundedIcon from '@mui/icons-material/LightModeRounded';
import BadgeRoundedIcon from '@mui/icons-material/BadgeRounded';
import GoogleIcon from './GoogleIcon';
import { LoginWithUsernameAndPassword } from '../../services/auth.service';
import { LoginSuccess, RootState } from '../../features/auth/auth.slice';
import { useSelector, useDispatch } from 'react-redux'
import { createSlice, Dispatch } from '@reduxjs/toolkit'
import { useNavigate } from "react-router-dom";
import { useSnackbar } from 'notistack';
import Modal from '@mui/joy/Modal';
import { DialogContent, DialogTitle, FormHelperText, ModalClose, ModalDialog } from '@mui/joy';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import { InfoOutlined } from '@mui/icons-material';
import * as yup from "yup";
import { RequestPasswordReset } from '../../services/email.service';
import { AxiosError } from 'axios';

interface FormElements extends HTMLFormControlsCollection {
  username: HTMLInputElement;
  password: HTMLInputElement;
  persistent: HTMLInputElement;
}
interface SignInFormElement extends HTMLFormElement {
  readonly elements: FormElements;
}
interface LoginData {
  username: String;
  password: String;
}
function ColorSchemeToggle(props: IconButtonProps) {
  const { onClick, ...other } = props;
  const { mode, setMode } = useColorScheme();
  const [mounted, setMounted] = React.useState(false);
  React.useEffect(() => {
    setMounted(true);
  }, []);
  if (!mounted) {
    return <IconButton size="sm" variant="outlined" color="neutral" disabled />;
  }
  return (
    <IconButton
      id="toggle-mode"
      size="sm"
      variant="outlined"
      color="neutral"
      aria-label="toggle light/dark mode"
      {...other}
      onClick={(event) => {
        if (mode === 'light') {
          setMode('dark');
        } else {
          setMode('light');
        }
        onClick?.(event);
      }}
    >
      {mode === 'light' ? <DarkModeRoundedIcon /> : <LightModeRoundedIcon />}
    </IconButton>
  );
}
const emailSchema = yup.object().shape({
  email: yup.string()
    .required("Email is required!")
    .email("Email is invalid!"),
})
const loginSchema = yup.object().shape({
  username: yup.string()
    .required("Username is required!"),
  password: yup.string()
    .required("Password is required!")
})

export default function JoySignInSideTemplate() {
  const isAuthenticated = useSelector((state: RootState) => state?.auth?.isAuthenticated);
  const isAuthLoaded = useSelector((state: RootState) => state.auth.isLoaded);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();
  const [open, setOpen] = React.useState<boolean>(false);
  const [uploading, setUploading] = React.useState<boolean>(false);
  const {
    register,
    handleSubmit,
    formState: { errors: errors }
  } = useForm({
    resolver: yupResolver(emailSchema),
  })
  const {
    register: registerLogin,
    handleSubmit: handleLoginSubmit,
    setValue,
    formState: {errors: loginErrors}
  } = useForm({
    resolver: yupResolver(loginSchema),
  })

  const login = async (e: any) => {
    try {
      setUploading(true);
      const data = {
        username: e.username,
        password: e.password,
      }
      const login = await LoginWithUsernameAndPassword(data);
      if (login?.data) {
        const loginData = login.data;
        dispatch(LoginSuccess(loginData));
        navigate('/home')
      }
      setUploading(false);
    }
    catch (err: any) {
      setValue("password", "");
      enqueueSnackbar(`${err.message}`, { variant: "error" });
      setUploading(false);
    }
  }
  const handlePasswordReset = async (e: any) => {
    console.log(e);
    const data = {
      email: e.email,
    }
    try {
      const result = await RequestPasswordReset(data);
      //Close the input tab
      setOpen(false);
      //Print success message
      enqueueSnackbar(`${result.message}`, { variant: "success" });
    }
    catch (error: any) {
      enqueueSnackbar(`Error while processing: ${error.response.data.message}`, { variant: "error" });
    }
  }
  React.useEffect(() => {
    if (isAuthenticated && isAuthLoaded) {
      navigate('/home');
    }
  }, [isAuthenticated, isAuthLoaded]);
  return (
    <CssVarsProvider defaultMode="dark" disableTransitionOnChange>
      <CssBaseline />
      <GlobalStyles
        styles={{
          ':root': {
            '--Collapsed-breakpoint': '769px', // form will stretch when viewport is below `769px`
            '--Cover-width': '50vw', // must be `vw` only
            '--Form-maxWidth': '800px',
            '--Transition-duration': '0.4s', // set to `none` to disable transition
          },
        }}
      />
      <Box
        sx={(theme) => ({
          width:
            'clamp(100vw - var(--Cover-width), (var(--Collapsed-breakpoint) - 100vw) * 999, 100vw)',
          transition: 'width var(--Transition-duration)',
          transitionDelay: 'calc(var(--Transition-duration) + 0.1s)',
          position: 'relative',
          zIndex: 1,
          display: 'flex',
          justifyContent: 'flex-end',
          backdropFilter: 'blur(12px)',
          backgroundColor: 'rgba(255 255 255 / 0.2)',
          [theme.getColorSchemeSelector('dark')]: {
            backgroundColor: 'rgba(19 19 24 / 0.4)',
          },
        })}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            minHeight: '100dvh',
            width:
              'clamp(var(--Form-maxWidth), (var(--Collapsed-breakpoint) - 100vw) * 999, 100%)',
            maxWidth: '100%',
            px: 2,
          }}
        >
          <Box
            component="header"
            sx={{
              py: 3,
              display: 'flex',
              alignItems: 'left',
              justifyContent: 'space-between',
            }}
          >
            <Box sx={{ gap: 2, display: 'flex', alignItems: 'center' }}>
              <IconButton variant="soft" color="primary" size="sm">
                <BadgeRoundedIcon />
              </IconButton>
              <Typography level="title-lg">Nice trip</Typography>
            </Box>
            <ColorSchemeToggle />
          </Box>
          <Box
            component="main"
            sx={{
              my: 'auto',
              py: 2,
              pb: 5,
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              width: 400,
              maxWidth: '100%',
              mx: 'auto',
              borderRadius: 'sm',
              '& form': {
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
              },
              [`& .${formLabelClasses.asterisk}`]: {
                visibility: 'hidden',
              },
            }}
          >
            <Stack gap={4} sx={{ mb: 2 }}>
              <Stack gap={1}>
                <Typography level="h3">Sign in</Typography>
                <Typography level="body-sm">
                  New one here?{' '}
                  <Link href="/register" level="title-sm">
                    Sign up!
                  </Link>
                </Typography>
              </Stack>
              <Button
                variant="soft"
                color="neutral"
                fullWidth
                startDecorator={<GoogleIcon />}
              >
                Continue with Google
              </Button>
            </Stack>
            <Divider
              sx={(theme) => ({
                [theme.getColorSchemeSelector('light')]: {
                  color: { xs: '#FFF', md: 'text.tertiary' },
                  '--Divider-lineColor': {
                    xs: '#FFF',
                    md: 'var(--joy-palette-divider)',
                  },
                },
              })}
            >
              or
            </Divider>
            <Stack gap={4} sx={{ mt: 2 }}>
              <form
                onSubmit={handleLoginSubmit(login)}
              >
                <FormControl required>
                  <FormLabel>Username</FormLabel>
                  <Input type="text" {...registerLogin("username")} />
                </FormControl>
                <FormControl required>
                  <FormLabel>Password</FormLabel>
                  <Input type="password" {...registerLogin("password")} />
                </FormControl>
                <Stack gap={4} sx={{ mt: 2 }}>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                    }}
                  >
                    <Checkbox size="sm" label="Remember me" name="persistent" />
                    <Link level="title-sm" onClick={() => setOpen(true)}>
                      Forgot your password?
                    </Link>
                  </Box>
                  <Button loading={uploading} type="submit" fullWidth>
                    Sign in
                  </Button>
                </Stack>
              </form>
            </Stack>
          </Box>
          <Box component="footer" sx={{ py: 3 }}>
            <Typography level="body-xs" textAlign="center">
              © NiceTrip {new Date().getFullYear()}
            </Typography>
          </Box>
        </Box>
      </Box>
      <Box
        sx={(theme) => ({
          height: '100%',
          position: 'fixed',
          right: 0,
          top: 0,
          bottom: 0,
          left: 'clamp(0px, (100vw - var(--Collapsed-breakpoint)) * 999, 100vw - var(--Cover-width))',
          transition:
            'background-image var(--Transition-duration), left var(--Transition-duration) !important',
          transitionDelay: 'calc(var(--Transition-duration) + 0.1s)',
          backgroundColor: 'background.level1',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          backgroundImage:
            'url(https://images.unsplash.com/photo-1527181152855-fc03fc7949c8?auto=format&w=1000&dpr=2)',
          [theme.getColorSchemeSelector('dark')]: {
            backgroundImage:
              'url(https://images.unsplash.com/photo-1572072393749-3ca9c8ea0831?auto=format&w=1000&dpr=2)',
          },
        })}
      />
      <Modal open={open} onClose={() => setOpen(false)}>
        <ModalDialog>
          <ModalClose />
          <DialogTitle>Reset my password</DialogTitle>
          <DialogContent>Please enter your email.</DialogContent>
          <form onSubmit={handleSubmit(handlePasswordReset)}>
            <Stack spacing={2}>
              <FormControl error={!!errors.email}>
                <Input type='text' {...register('email')} />
                {errors.email &&
                  <FormHelperText>
                    <InfoOutlined />
                    {errors.email.message}
                  </FormHelperText>
                }
              </FormControl>
              <Button type="submit">Submit</Button>
            </Stack>
          </form>
        </ModalDialog>
      </Modal>
    </CssVarsProvider>
  );
}
